import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { MLAnalyticsApi } from '../../services/api/MLAnalyticsApi';
import {
  MetricCard, LoadingChart, EmptyState
} from '../../components/analytics/SharedAnalyticsComponents';
import PdfExportButton from '../../components/analytics/PdfExportButton';
import { ReportGenerator } from '../../utils/ReportGenerator';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, ScatterChart,
  Scatter, ZAxis, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';

// ── Constants ───────────────────────────────────────────────────

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'rfm', label: 'RFM Segments' },
  { key: 'clusters', label: 'K-Means Clusters' },
  { key: 'features', label: 'Feature Importance' },
  { key: 'explorer', label: 'User Explorer' },
];

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316', '#a855f7', '#10b981'];

const RFM_SEGMENT_COLORS = {
  'Champions':          '#22c55e',
  'Loyal Users':        '#6366f1',
  'Potential Loyalists': '#06b6d4',
  'Recent Users':       '#3b82f6',
  'Promising':          '#8b5cf6',
  'Need Attention':     '#f59e0b',
  'About to Sleep':     '#f97316',
  'At Risk':            '#ef4444',
  "Can't Lose Them":    '#dc2626',
  'Hibernating':        '#9ca3af',
  'Lost':               '#6b7280',
};

const RFM_SEGMENT_ORDER = [
  'Champions', 'Loyal Users', 'Potential Loyalists', 'Recent Users',
  'Promising', 'Need Attention', 'About to Sleep', 'At Risk',
  "Can't Lose Them", 'Hibernating', 'Lost',
];

// ── Main Component ──────────────────────────────────────────────

export default function SegmentationDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [rfmData, setRfmData] = useState(null);
  const [clusterData, setClusterData] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [userSegments, setUserSegments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [training, setTraining] = useState(false);
  const [trainResult, setTrainResult] = useState(null);

  // Explorer filters
  const [rfmFilter, setRfmFilter] = useState('');
  const [clusterFilter, setClusterFilter] = useState('');
  const [explorerPage, setExplorerPage] = useState(0);

  const dashboardRef = useRef(null);
  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');

  // ── Data Loading ────────────────────────────────────────────

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rfm, clusters, dist] = await Promise.all([
        MLAnalyticsApi.getRFMSegments().catch(() => null),
        MLAnalyticsApi.getSegments().catch(() => null),
        MLAnalyticsApi.getSegmentDistribution().catch(() => null),
      ]);
      setRfmData(rfm);
      setClusterData(clusters);
      setDistribution(dist);
    } catch (e) {
      console.error('Failed to load segmentation overview:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRfm = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await MLAnalyticsApi.getRFMSegments();
      setRfmData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClusters = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await MLAnalyticsApi.getSegments();
      setClusterData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadExplorer = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await MLAnalyticsApi.getUserSegments({
        rfmSegment: rfmFilter || undefined,
        clusterId: clusterFilter !== '' ? Number(clusterFilter) : undefined,
        page: explorerPage,
        pageSize: 50,
      });
      setUserSegments(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [rfmFilter, clusterFilter, explorerPage]);

  useEffect(() => {
    const loaders = {
      overview: loadOverview,
      rfm: loadRfm,
      clusters: loadClusters,
      features: loadClusters, // feature importance comes from the segments endpoint
      explorer: loadExplorer,
    };
    loaders[activeTab]?.();
  }, [activeTab, loadOverview, loadRfm, loadClusters, loadExplorer]);

  // ── Training ──────────────────────────────────────────────────

  const handleRetrain = async () => {
    if (!window.confirm('Re-train segmentation models? This may take several minutes.')) return;
    try {
      setTraining(true);
      setTrainResult(null);
      await MLAnalyticsApi.trainModel('segments');
      await MLAnalyticsApi.trainModel('rfm');
      setTrainResult({ success: true });
      // Refresh
      loadOverview();
    } catch (e) {
      setTrainResult({ success: false, error: e.message });
    } finally {
      setTraining(false);
    }
  };

  // ── PDF Export ────────────────────────────────────────────────

  const handleExport = async () => {
    const gen = new ReportGenerator('User Segmentation Report');
    gen.addMetricsCards([
      { label: 'Total Users', value: rfmData?.users?.length || distribution?.total_users || '-' },
      { label: 'RFM Segments', value: Object.keys(rfmData?.distribution || {}).length || '-' },
      { label: 'K-Means Clusters', value: clusterData?.cluster_summaries?.length || '-' },
    ]);
    await gen.captureChart(dashboardRef.current, 'Segmentation Dashboard Snapshot');
    return gen.save('segmentation-report.pdf');
  };

  // ── Tab: Overview ─────────────────────────────────────────────

  const renderOverview = () => {
    if (loading) return <LoadingChart height={400} />;
    const rfmDist = rfmData?.distribution || {};
    const clusters = clusterData?.cluster_summaries || [];
    const totalRfmUsers = Object.values(rfmDist).reduce((a, b) => a + b, 0);
    const totalClusterUsers = clusters.reduce((a, c) => a + (c.size || 0), 0);

    // Top-level KPIs
    const champCount = rfmDist['Champions'] || 0;
    const atRiskCount = (rfmDist['At Risk'] || 0) + (rfmDist["Can't Lose Them"] || 0);
    const healthyPct = totalRfmUsers > 0
      ? (((rfmDist['Champions'] || 0) + (rfmDist['Loyal Users'] || 0) + (rfmDist['Potential Loyalists'] || 0)) / totalRfmUsers * 100).toFixed(1)
      : 0;

    // RFM pie data
    const rfmPieData = RFM_SEGMENT_ORDER
      .filter(s => rfmDist[s])
      .map(s => ({ name: s, value: rfmDist[s], fill: RFM_SEGMENT_COLORS[s] || '#9ca3af' }));

    // Cluster bar data
    const clusterBarData = clusters.map((c, i) => ({
      name: c.label || `Cluster ${c.cluster_id}`,
      users: c.size,
      fill: COLORS[i % COLORS.length],
    }));

    return (
      <div>
        {/* KPI Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          <MetricCard label="Total Segmented Users" value={totalRfmUsers || totalClusterUsers || '-'} color="#6366f1" />
          <MetricCard label="Champions" value={champCount} color="#22c55e" subtitle="Highest-value segment" />
          <MetricCard label="At Risk / Can't Lose" value={atRiskCount} color="#ef4444" subtitle="Need immediate attention" />
          <MetricCard label="Healthy User %" value={`${healthyPct}%`} color="#06b6d4" subtitle="Champions + Loyal + Potential" />
          <MetricCard label="Active Clusters" value={clusters.length || '-'} color="#8b5cf6" />
        </div>

        {/* Two-column: RFM Pie + Cluster Bar */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
          <div style={cardStyle}>
            <h3 style={cardTitle}>RFM Segment Distribution</h3>
            {rfmPieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={rfmPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={50}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={{ strokeWidth: 1 }}
                    >
                      {rfmPieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                    </Pie>
                    <Tooltip formatter={(val) => `${val} users`} />
                  </PieChart>
                </ResponsiveContainer>
                <RfmLegend distribution={rfmDist} total={totalRfmUsers} />
              </>
            ) : <EmptyState message="No RFM data — train the RFM model first" icon="🔬" />}
          </div>

          <div style={cardStyle}>
            <h3 style={cardTitle}>K-Means Cluster Sizes</h3>
            {clusterBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={clusterBarData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip formatter={(val) => `${val} users`} />
                  <Bar dataKey="users" name="Users" radius={[0, 4, 4, 0]}>
                    {clusterBarData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyState message="No cluster data — train the segmentation model first" icon="🧩" />}
          </div>
        </div>

        {/* Quick insight cards */}
        {rfmPieData.length > 0 && (
          <div style={cardStyle}>
            <h3 style={cardTitle}>Key Business Insights</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
              <InsightBox
                icon="🏆"
                title="Retention Opportunity"
                text={`${champCount} Champions identified. Target them with loyalty perks and referral programs to amplify organic growth.`}
                color="#22c55e"
              />
              <InsightBox
                icon="⚠️"
                title="Churn Prevention"
                text={`${atRiskCount} users are At Risk or Can't Lose. Consider re-engagement campaigns, personalized outreach, or special offers.`}
                color="#ef4444"
              />
              <InsightBox
                icon="🚀"
                title="Growth Potential"
                text={`${rfmDist['Promising'] || 0} Promising and ${rfmDist['Potential Loyalists'] || 0} Potential Loyalists — nurture with onboarding flows and feature education.`}
                color="#6366f1"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Tab: RFM Segments ─────────────────────────────────────────

  const renderRfm = () => {
    if (loading || !rfmData) return <LoadingChart height={400} />;
    const { distribution: rfmDist = {}, users = [], scored_at } = rfmData;
    const totalUsers = Object.values(rfmDist).reduce((a, b) => a + b, 0);

    // Horizontal bar chart — ordered by segment health
    const barData = RFM_SEGMENT_ORDER
      .filter(s => rfmDist[s])
      .map(s => ({
        name: s,
        count: rfmDist[s],
        pct: totalUsers > 0 ? ((rfmDist[s] / totalUsers) * 100).toFixed(1) : 0,
        fill: RFM_SEGMENT_COLORS[s],
      }));

    // RFM heatmap: aggregate users by (R, F) to show density
    const heatmapData = buildRfmHeatmap(users);

    // Radar chart: average RFM scores per segment
    const radarData = buildSegmentRadar(users);

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>RFM Segmentation</h3>
            {scored_at && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>Last scored: {new Date(scored_at).toLocaleString()}</p>}
          </div>
          {isAdmin && (
            <button onClick={() => handleRetrainSingle('rfm')} disabled={training} style={{ ...btnPrimary, opacity: training ? 0.6 : 1 }}>
              {training ? 'Training…' : 'Retrain RFM'}
            </button>
          )}
        </div>

        {/* Segment distribution bar */}
        <div style={{ ...cardStyle, marginBottom: 24 }}>
          <h4 style={sectionTitle}>Segment Distribution</h4>
          {barData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(250, barData.length * 38)}>
              <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={150} />
                <Tooltip content={<RfmBarTooltip total={totalUsers} />} />
                <Bar dataKey="count" name="Users" radius={[0, 4, 4, 0]}>
                  {barData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState message="No RFM data" icon="🔬" />}
        </div>

        {/* Two-column: Heatmap + Radar */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
          {/* RFM Heatmap */}
          <div style={cardStyle}>
            <h4 style={sectionTitle}>Recency × Frequency Heatmap</h4>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: '#9ca3af' }}>Cell color intensity = user count for that (R, F) quintile pair</p>
            {heatmapData.length > 0 ? (
              <RfmHeatmapGrid data={heatmapData} />
            ) : <EmptyState message="Need user-level data for heatmap" icon="🗺️" />}
          </div>

          {/* Segment Radar */}
          <div style={cardStyle}>
            <h4 style={sectionTitle}>Average R·F·M Scores by Segment</h4>
            {radarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="segment" tick={{ fontSize: 10 }} />
                  <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                  <Radar name="Recency" dataKey="R" stroke="#6366f1" fill="#6366f1" fillOpacity={0.15} />
                  <Radar name="Frequency" dataKey="F" stroke="#22c55e" fill="#22c55e" fillOpacity={0.15} />
                  <Radar name="Monetary" dataKey="M" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            ) : <EmptyState message="Insufficient data for radar" icon="📡" />}
          </div>
        </div>

        {/* Segment detail cards */}
        <div style={cardStyle}>
          <h4 style={sectionTitle}>Segment Breakdown</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {barData.map((seg) => (
              <div key={seg.name} style={{ padding: 12, borderRadius: 8, border: `1px solid ${seg.fill}30`, background: `${seg.fill}08` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: seg.fill }}>{seg.name}</span>
                  <span style={{ ...badgeBase, background: `${seg.fill}18`, color: seg.fill }}>{seg.count}</span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{seg.pct}% of total users</div>
                <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: '#e5e7eb' }}>
                  <div style={{ height: 4, borderRadius: 2, background: seg.fill, width: `${seg.pct}%`, transition: 'width 0.6s ease' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── Tab: K-Means Clusters ─────────────────────────────────────

  const renderClusters = () => {
    if (loading || !clusterData) return <LoadingChart height={400} />;
    const { cluster_summaries: clusters = [], users = [] } = clusterData;

    const distData = clusters.map((c, i) => ({
      name: c.label || `Cluster ${c.cluster_id}`,
      value: c.size,
      fill: COLORS[i % COLORS.length],
    }));

    // Centroid comparison: build a table of each feature vs each cluster
    const centroidFeatures = clusters.length > 0 && clusters[0].centroid
      ? Object.keys(clusters[0].centroid)
      : [];

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>K-Means Clusters</h3>
          {isAdmin && (
            <button onClick={() => handleRetrainSingle('segments')} disabled={training} style={{ ...btnPrimary, opacity: training ? 0.6 : 1 }}>
              {training ? 'Training…' : 'Retrain Clusters'}
            </button>
          )}
        </div>

        {/* Distribution pie + scatter */}
        <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: 24, marginBottom: 24 }}>
          <div style={cardStyle}>
            <h4 style={sectionTitle}>Cluster Sizes</h4>
            {distData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={distData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={45} paddingAngle={2}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                    {distData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip formatter={v => `${v} users`} />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyState message="No cluster data" icon="🧩" />}
          </div>

          {/* User scatter — pick first two centroid dimensions */}
          <div style={cardStyle}>
            <h4 style={sectionTitle}>User Distribution (first 2 features)</h4>
            {users.length > 0 && centroidFeatures.length >= 2 ? (
              <ResponsiveContainer width="100%" height={280}>
                <ScatterChart margin={{ bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="x" name={centroidFeatures[0]} type="number" tick={{ fontSize: 11 }} label={{ value: centroidFeatures[0].replace(/_/g, ' '), position: 'insideBottom', offset: -5, fontSize: 11 }} />
                  <YAxis dataKey="y" name={centroidFeatures[1]} type="number" tick={{ fontSize: 11 }} label={{ value: centroidFeatures[1].replace(/_/g, ' '), angle: -90, position: 'insideLeft', fontSize: 11 }} />
                  <ZAxis range={[30, 30]} />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<ScatterTooltip features={centroidFeatures} />} />
                  {clusters.map((c, ci) => {
                    const clusterUsers = users
                      .filter(u => u.cluster_id === c.cluster_id)
                      .slice(0, 200) // limit for performance
                      .map(u => ({ x: u[centroidFeatures[0]] ?? 0, y: u[centroidFeatures[1]] ?? 0, email: u.email, cluster: c.label }));
                    return (
                      <Scatter key={ci} name={c.label || `Cluster ${c.cluster_id}`} data={clusterUsers} fill={COLORS[ci % COLORS.length]} opacity={0.7} />
                    );
                  })}
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </ScatterChart>
              </ResponsiveContainer>
            ) : <EmptyState message="Need user data for scatter plot" icon="📊" />}
          </div>
        </div>

        {/* Cluster detail cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
          {clusters.map((c, i) => (
            <div key={c.cluster_id} style={{ ...cardStyle, borderLeft: `4px solid ${COLORS[i % COLORS.length]}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{c.label || `Cluster ${c.cluster_id}`}</h4>
                <span style={{ ...badgeBase, background: `${COLORS[i % COLORS.length]}18`, color: COLORS[i % COLORS.length] }}>{c.size} users</span>
              </div>
              {c.centroid && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 12, color: '#374151' }}>
                  {Object.entries(c.centroid).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                      <span style={{ color: '#6b7280' }}>{k.replace(/_/g, ' ')}:</span>
                      <span style={{ fontWeight: 500 }}>{typeof v === 'number' ? v.toFixed(2) : v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Centroid comparison table */}
        {centroidFeatures.length > 0 && (
          <div style={cardStyle}>
            <h4 style={sectionTitle}>Centroid Comparison</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Feature</th>
                    {clusters.map((c, i) => (
                      <th key={c.cluster_id} style={{ ...thStyle, color: COLORS[i % COLORS.length] }}>
                        {c.label || `Cluster ${c.cluster_id}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {centroidFeatures.map((feat, fi) => {
                    const values = clusters.map(c => c.centroid?.[feat] ?? 0);
                    const maxVal = Math.max(...values);
                    return (
                      <tr key={feat} style={fi % 2 === 0 ? { background: 'var(--table-stripe, #f9fafb)' } : {}}>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>{feat.replace(/_/g, ' ')}</td>
                        {clusters.map((c, ci) => {
                          const val = c.centroid?.[feat] ?? 0;
                          const isMax = val === maxVal && maxVal > 0;
                          return (
                            <td key={ci} style={{ ...tdStyle, fontWeight: isMax ? 700 : 400, color: isMax ? COLORS[ci % COLORS.length] : undefined }}>
                              {typeof val === 'number' ? val.toFixed(2) : val}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Tab: Feature Importance ───────────────────────────────────

  const renderFeatures = () => {
    if (loading || !clusterData) return <LoadingChart height={400} />;
    const { feature_importance = {}, cluster_summaries: clusters = [] } = clusterData;

    const importanceEntries = Object.entries(feature_importance)
      .sort(([, a], [, b]) => b - a);

    const barData = importanceEntries.map(([name, value]) => ({
      name: name.replace(/_/g, ' '),
      importance: Number((value * 100).toFixed(1)),
    }));

    const topFeatures = importanceEntries.slice(0, 5);

    return (
      <div>
        <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600 }}>Feature Importance (Random Forest)</h3>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280' }}>
          Shows which user attributes contribute most to distinguishing cluster assignments. Higher importance = more discriminative feature.
        </p>

        {barData.length > 0 ? (
          <>
            {/* Importance bar chart */}
            <div style={{ ...cardStyle, marginBottom: 24 }}>
              <h4 style={sectionTitle}>All Features Ranked</h4>
              <ResponsiveContainer width="100%" height={Math.max(300, barData.length * 32)}>
                <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 12 }} domain={[0, 'auto']} tickFormatter={v => `${v}%`} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={180} />
                  <Tooltip formatter={v => `${v}%`} />
                  <Bar dataKey="importance" name="Importance %" radius={[0, 4, 4, 0]}>
                    {barData.map((_, i) => (
                      <Cell key={i} fill={i < 5 ? '#6366f1' : i < 10 ? '#8b5cf6' : '#c4b5fd'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top feature insight cards */}
            <div style={cardStyle}>
              <h4 style={sectionTitle}>Top Differentiating Features</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                {topFeatures.map(([name, value], i) => (
                  <div key={name} style={{ padding: 14, borderRadius: 8, background: `${COLORS[i]}08`, border: `1px solid ${COLORS[i]}25` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>#{i + 1} {name.replace(/_/g, ' ')}</span>
                      <span style={{ ...badgeBase, background: `${COLORS[i]}15`, color: COLORS[i] }}>{(value * 100).toFixed(1)}%</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: '#e5e7eb' }}>
                      <div style={{ height: 6, borderRadius: 3, background: COLORS[i], width: `${(value / importanceEntries[0][1]) * 100}%`, transition: 'width 0.6s ease' }} />
                    </div>
                    {/* Show how each cluster scores on this feature */}
                    {clusters.length > 0 && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {clusters.map((c, ci) => (
                          <span key={ci}>
                            <span style={{ color: COLORS[ci % COLORS.length], fontWeight: 500 }}>{c.label || `C${c.cluster_id}`}</span>
                            : {c.centroid?.[name] != null ? Number(c.centroid[name]).toFixed(1) : '-'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : <EmptyState message="Feature importance is available after training the segmentation model" icon="🎯" />}
      </div>
    );
  };

  // ── Tab: User Explorer ────────────────────────────────────────

  const renderExplorer = () => {
    const rows = userSegments?.content || userSegments || [];
    const isArray = Array.isArray(rows);
    const items = isArray ? rows : [];

    // Collect unique RFM segments and cluster IDs for filters
    const rfmSegments = [...new Set(items.map(u => u.rfmSegment || u.rfm_segment).filter(Boolean))];
    const clusterIds = [...new Set(items.map(u => u.clusterId ?? u.cluster_id).filter(v => v != null))];

    return (
      <div>
        <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600 }}>User Segment Explorer</h3>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: '#6b7280' }}>RFM Segment:</label>
            <select value={rfmFilter} onChange={e => { setRfmFilter(e.target.value); setExplorerPage(0); }} style={selectStyle}>
              <option value="">All</option>
              {RFM_SEGMENT_ORDER.map(s => <option key={s} value={s}>{s}</option>)}
              {rfmSegments.filter(s => !RFM_SEGMENT_ORDER.includes(s)).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: '#6b7280' }}>Cluster:</label>
            <select value={clusterFilter} onChange={e => { setClusterFilter(e.target.value); setExplorerPage(0); }} style={selectStyle}>
              <option value="">All</option>
              {clusterIds.sort().map(id => <option key={id} value={id}>Cluster {id}</option>)}
            </select>
          </div>
          <button onClick={loadExplorer} style={btnSecondary}>Refresh</button>
        </div>

        {/* Table */}
        {loading ? <LoadingChart height={300} /> : items.length > 0 ? (
          <div style={cardStyle}>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>User</th>
                    <th style={thStyle}>RFM Score</th>
                    <th style={thStyle}>RFM Segment</th>
                    <th style={thStyle}>Cluster</th>
                    <th style={thStyle}>R</th>
                    <th style={thStyle}>F</th>
                    <th style={thStyle}>M</th>
                    <th style={thStyle}>Activity Trend</th>
                    <th style={thStyle}>Segmented</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((u, i) => {
                    const rfmSeg = u.rfmSegment || u.rfm_segment || '-';
                    const segColor = RFM_SEGMENT_COLORS[rfmSeg] || '#6b7280';
                    return (
                      <tr key={i} style={i % 2 === 0 ? { background: 'var(--table-stripe, #f9fafb)' } : {}}>
                        <td style={tdStyle}>{u.email || u.userId || u.user_id}</td>
                        <td style={tdStyle}><code style={{ fontSize: 12 }}>{u.rfmScore || u.rfm_score || '-'}</code></td>
                        <td style={tdStyle}>
                          <span style={{ ...badgeBase, background: `${segColor}18`, color: segColor }}>{rfmSeg}</span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ ...badgeBase, background: `${COLORS[(u.clusterId ?? u.cluster_id ?? 0) % COLORS.length]}18`, color: COLORS[(u.clusterId ?? u.cluster_id ?? 0) % COLORS.length] }}>
                            {u.clusterLabel || u.cluster_label || `Cluster ${u.clusterId ?? u.cluster_id ?? '-'}`}
                          </span>
                        </td>
                        <td style={tdStyle}>{u.rfmRecencyScore ?? u.rfm_recency_score ?? '-'}</td>
                        <td style={tdStyle}>{u.rfmFrequencyScore ?? u.rfm_frequency_score ?? '-'}</td>
                        <td style={tdStyle}>{u.rfmMonetaryScore ?? u.rfm_monetary_score ?? '-'}</td>
                        <td style={tdStyle}>
                          <TrendBadge trend={u.activityTrend || u.activity_trend} />
                        </td>
                        <td style={{ ...tdStyle, fontSize: 11, color: '#9ca3af' }}>
                          {u.segmentedAt || u.segmented_at ? new Date(u.segmentedAt || u.segmented_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>
                Page {explorerPage + 1} · {items.length} rows
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setExplorerPage(p => Math.max(0, p - 1))} disabled={explorerPage === 0} style={{ ...btnSecondary, opacity: explorerPage === 0 ? 0.5 : 1 }}>
                  ← Previous
                </button>
                <button onClick={() => setExplorerPage(p => p + 1)} disabled={items.length < 50} style={{ ...btnSecondary, opacity: items.length < 50 ? 0.5 : 1 }}>
                  Next →
                </button>
              </div>
            </div>
          </div>
        ) : <EmptyState message="No user segments found — train models first or adjust filters" icon="🔍" />}
      </div>
    );
  };

  // ── Single model train helper ─────────────────────────────────

  const handleRetrainSingle = async (modelName) => {
    if (!window.confirm(`Retrain the ${modelName} model?`)) return;
    try {
      setTraining(true);
      setTrainResult(null);
      await MLAnalyticsApi.trainModel(modelName);
      setTrainResult({ success: true });
    } catch (e) {
      setTrainResult({ success: false, error: e.message });
    } finally {
      setTraining(false);
    }
  };

  // ── Tab dispatch ──────────────────────────────────────────────

  const tabContent = {
    overview: renderOverview,
    rfm: renderRfm,
    clusters: renderClusters,
    features: renderFeatures,
    explorer: renderExplorer,
  };

  // ── Main Render ───────────────────────────────────────────────

  return (
    <div ref={dashboardRef} style={{ padding: 24, maxWidth: 1400, margin: '0 auto', height: '100%', overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>User Segmentation</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: '#6b7280' }}>
            RFM scoring &amp; K-Means clustering — understand user behavior, target campaigns, and reduce churn
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdmin && (
            <button onClick={handleRetrain} disabled={training} style={{ ...btnPrimary, opacity: training ? 0.6 : 1 }}>
              {training ? 'Training…' : '🔄 Retrain All'}
            </button>
          )}
          <PdfExportButton onExport={handleExport} label="Export Report" />
        </div>
      </div>

      {/* Training result banner */}
      {trainResult && (
        <div style={{ ...cardStyle, marginBottom: 16, background: trainResult.success ? '#f0fdf4' : '#fef2f2', borderLeft: `4px solid ${trainResult.success ? '#22c55e' : '#ef4444'}` }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>
            {trainResult.success ? '✅ Segmentation models retrained successfully' : `❌ Training failed: ${trainResult.error}`}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: 24 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '10px 20px', fontSize: 14, fontWeight: activeTab === t.key ? 600 : 400,
              color: activeTab === t.key ? '#6366f1' : '#6b7280', background: 'none', border: 'none',
              borderBottom: activeTab === t.key ? '2px solid #6366f1' : '2px solid transparent',
              cursor: 'pointer', marginBottom: -2, transition: 'all 0.2s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Error bar */}
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#991b1b', fontSize: 14 }}>
          ⚠️ {error}
        </div>
      )}

      {/* Tab content */}
      {tabContent[activeTab]?.()}
    </div>
  );
}

// ── Helper Components ──────────────────────────────────────────

function InsightBox({ icon, title, text, color }) {
  return (
    <div style={{ padding: 14, borderRadius: 8, background: `${color}06`, border: `1px solid ${color}20` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontWeight: 600, fontSize: 14, color }}>{title}</span>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{text}</p>
    </div>
  );
}

function RfmLegend({ distribution, total }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
      {RFM_SEGMENT_ORDER.filter(s => distribution[s]).map(s => (
        <span key={s} style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: RFM_SEGMENT_COLORS[s], display: 'inline-block' }} />
          {s}: {distribution[s]} ({total > 0 ? ((distribution[s] / total) * 100).toFixed(0) : 0}%)
        </span>
      ))}
    </div>
  );
}

function RfmBarTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: d.fill }}>{d.name}</div>
      <div>{d.count} users ({total > 0 ? ((d.count / total) * 100).toFixed(1) : 0}%)</div>
    </div>
  );
}

function RfmHeatmapGrid({ data }) {
  // data = array of { r: 1-5, f: 1-5, count }
  const maxCount = Math.max(...data.map(d => d.count), 1);
  return (
    <div>
      <div style={{ display: 'flex', gap: 0, fontSize: 11, color: '#6b7280', marginBottom: 4, paddingLeft: 36 }}>
        {[1, 2, 3, 4, 5].map(f => (
          <div key={f} style={{ width: 48, textAlign: 'center' }}>F={f}</div>
        ))}
      </div>
      {[5, 4, 3, 2, 1].map(r => (
        <div key={r} style={{ display: 'flex', gap: 0, alignItems: 'center' }}>
          <div style={{ width: 36, fontSize: 11, color: '#6b7280', textAlign: 'right', paddingRight: 6 }}>R={r}</div>
          {[1, 2, 3, 4, 5].map(f => {
            const cell = data.find(d => d.r === r && d.f === f);
            const count = cell?.count || 0;
            const intensity = count / maxCount;
            return (
              <div
                key={f}
                title={`R=${r}, F=${f}: ${count} users`}
                style={{
                  width: 48, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: count > 0 ? `rgba(99, 102, 241, ${0.1 + intensity * 0.8})` : '#f9fafb',
                  color: intensity > 0.5 ? '#fff' : '#374151', fontSize: 11, fontWeight: count > 0 ? 600 : 400,
                  border: '1px solid #e5e7eb',
                }}
              >
                {count || ''}
              </div>
            );
          })}
        </div>
      ))}
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#9ca3af' }}>
        <span>Low</span>
        <div style={{ display: 'flex', gap: 1 }}>
          {[0.1, 0.3, 0.5, 0.7, 0.9].map(op => (
            <div key={op} style={{ width: 16, height: 10, background: `rgba(99, 102, 241, ${op})`, borderRadius: 2 }} />
          ))}
        </div>
        <span>High</span>
      </div>
    </div>
  );
}

function TrendBadge({ trend }) {
  if (!trend) return <span style={{ color: '#9ca3af' }}>-</span>;
  const config = {
    growing: { bg: '#dcfce7', color: '#166534', icon: '↗' },
    stable: { bg: '#eff6ff', color: '#1e40af', icon: '→' },
    declining: { bg: '#fef2f2', color: '#991b1b', icon: '↘' },
    new: { bg: '#f0fdf4', color: '#166534', icon: '✦' },
  };
  const c = config[trend] || config.stable;
  return (
    <span style={{ ...badgeBase, background: c.bg, color: c.color }}>
      {c.icon} {trend}
    </span>
  );
}

function ScatterTooltip({ active, payload, features }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
      {d.email && <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.email}</div>}
      {d.cluster && <div style={{ marginBottom: 4, color: '#6b7280' }}>{d.cluster}</div>}
      <div>{features?.[0]}: {d.x?.toFixed(2)}</div>
      <div>{features?.[1]}: {d.y?.toFixed(2)}</div>
    </div>
  );
}

// ── RFM Heatmap Data Builder ────────────────────────────────────

function buildRfmHeatmap(users) {
  if (!users?.length) return [];
  const grid = {};
  for (const u of users) {
    const r = u.rfm_recency_score ?? u.rfmRecencyScore;
    const f = u.rfm_frequency_score ?? u.rfmFrequencyScore;
    if (r == null || f == null) continue;
    const key = `${r}-${f}`;
    grid[key] = (grid[key] || 0) + 1;
  }
  return Object.entries(grid).map(([key, count]) => {
    const [r, f] = key.split('-').map(Number);
    return { r, f, count };
  });
}

function buildSegmentRadar(users) {
  if (!users?.length) return [];
  const segAcc = {};
  for (const u of users) {
    const seg = u.rfm_segment || u.rfmSegment;
    if (!seg) continue;
    if (!segAcc[seg]) segAcc[seg] = { R: 0, F: 0, M: 0, n: 0 };
    segAcc[seg].R += (u.rfm_recency_score ?? u.rfmRecencyScore ?? 0);
    segAcc[seg].F += (u.rfm_frequency_score ?? u.rfmFrequencyScore ?? 0);
    segAcc[seg].M += (u.rfm_monetary_score ?? u.rfmMonetaryScore ?? 0);
    segAcc[seg].n += 1;
  }
  return Object.entries(segAcc)
    .filter(([, v]) => v.n > 0)
    .map(([seg, v]) => ({
      segment: seg.length > 12 ? seg.slice(0, 11) + '…' : seg,
      R: +(v.R / v.n).toFixed(1),
      F: +(v.F / v.n).toFixed(1),
      M: +(v.M / v.n).toFixed(1),
    }));
}

// ── Shared Styles ──────────────────────────────────────────────

const cardStyle = { background: 'var(--card-bg, #fff)', borderRadius: 8, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid var(--border-light, #e5e7eb)' };
const cardTitle = { margin: '0 0 16px', fontSize: 16, fontWeight: 600 };
const sectionTitle = { margin: '0 0 12px', fontSize: 14, fontWeight: 600 };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid var(--border-light, #e5e7eb)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase' };
const tdStyle = { padding: '8px 12px', borderBottom: '1px solid var(--border-light, #f3f4f6)' };
const badgeBase = { padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500, textTransform: 'capitalize' };
const selectStyle = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border-light, #d1d5db)', fontSize: 13, background: 'var(--card-bg, #fff)', color: 'var(--text-primary, #111)', cursor: 'pointer' };
const btnPrimary = { padding: '8px 20px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer' };
const btnSecondary = { padding: '6px 16px', borderRadius: 6, border: '1px solid var(--border-light, #d1d5db)', background: 'var(--card-bg, #fff)', color: 'var(--text-primary, #374151)', fontSize: 13, cursor: 'pointer' };
